import { PendingUpload, UploadStatus } from '../types/uploads';

export type WorkLifecycleStatus =
  | 'draft'
  | 'uploading'
  | 'in_review'
  | 'rejected'
  | 'processing'
  | 'ready_to_publish'
  | 'published'
  | 'archived';

export interface WorkStatusInput {
  workStatus: 'draft' | 'published' | 'archived';
  linkedUpload: PendingUpload | null;
  muxReady?: boolean;
}

const PIPELINE_ACTIVE: UploadStatus[] = ['uploading', 'scanning', 'human_review', 'ingesting'];

export function getUploadStatusLabel(status: UploadStatus): string {
  const labels: Record<UploadStatus, string> = {
    draft: 'Draft',
    uploading: 'Uploading',
    scanning: 'Scanning',
    human_review: 'In Review',
    approved: 'Approved',
    rejected: 'Rejected',
    ingesting: 'Processing',
    ingested: 'Live',
    error: 'Error',
  };
  return labels[status] ?? status;
}

export function canSubmitForReview(upload: PendingUpload | null): boolean {
  if (!upload) return false;
  if (upload.upload_status === 'rejected') return false;
  return ['scanning', 'approved', 'draft'].includes(upload.upload_status) && Boolean(upload.r2_key || upload.storage_path);
}

export function canPublishWork({ workStatus, linkedUpload, muxReady }: WorkStatusInput): { ok: boolean; reason?: string } {
  if (workStatus === 'archived') return { ok: false, reason: 'Content is archived' };
  if (workStatus === 'published') return { ok: false, reason: 'Already published' };
  if (!linkedUpload) return { ok: false, reason: 'No video asset attached' };
  if (linkedUpload.upload_status === 'rejected') {
    return { ok: false, reason: linkedUpload.human_review?.notes || 'Video was rejected' };
  }
  if (PIPELINE_ACTIVE.includes(linkedUpload.upload_status)) {
    return { ok: false, reason: `Video is ${getUploadStatusLabel(linkedUpload.upload_status).toLowerCase()}` };
  }
  if (linkedUpload.upload_status !== 'ingested') {
    return { ok: false, reason: `Video is ${getUploadStatusLabel(linkedUpload.upload_status).toLowerCase()}` };
  }
  if (!muxReady) return { ok: false, reason: 'Video is not yet ready for playback' };
  return { ok: true };
}

export function getWorkLifecycleStatus(input: WorkStatusInput): WorkLifecycleStatus {
  const { workStatus, linkedUpload, muxReady } = input;
  if (workStatus === 'archived') return 'archived';
  if (workStatus === 'published') return 'published';
  if (!linkedUpload) return 'draft';
  if (linkedUpload.upload_status === 'rejected') return 'rejected';
  if (PIPELINE_ACTIVE.includes(linkedUpload.upload_status)) {
    return linkedUpload.upload_status === 'human_review' ? 'in_review' : 'uploading';
  }
  if (linkedUpload.upload_status === 'ingested' && muxReady) return 'ready_to_publish';
  if (['ingested', 'ingesting', 'approved'].includes(linkedUpload.upload_status)) return 'processing';
  return 'draft';
}
